import PolarisIcon from '@/components/_common/PolarisIcon';
import styled from '@emotion/styled';
import { TimelineDot } from '@mui/lab';
import JobTimerIcon from './JobTimerIcon';
import { Tooltip } from '@mui/material';

interface JobMilestoneDotProps {
  status: string;
  cancelled?: boolean;
  isFinalCompleted: boolean;
  isAssigned: boolean;
}

const JobMilestoneDot = ({ status, cancelled = false, isFinalCompleted = false, isAssigned = false }: JobMilestoneDotProps) => {
  if (cancelled) {
    return (
      <CancelledTimelineDot>
        <PolarisIcon icon="close_filled" height={18} />
      </CancelledTimelineDot>
    );
  }

  switch (status) {
    case 'active':
      return (
        <TimerTimelineDot assigned={+isAssigned}>
          <JobTimerIcon ready={isAssigned} />
        </TimerTimelineDot>
      );
    case 'complete':
    case 'accepted':
      if (isFinalCompleted) {
        return (
          <FinalTimelineDot color="success">
            <PolarisIcon icon="check" height={24} />
          </FinalTimelineDot>
        );
      }
      return <CompleteTimelineDot color="success" />;
    case 'failed':
    case 'rejected':
      return (
        <FailedTimelineDot color="error">
          <PolarisIcon icon="error_filled" height={18} />
        </FailedTimelineDot>
      );
    case 'hold':
      return (
        <HoldTimelineDot>
          <Tooltip title="This job has an embargo date set">
            <PolarisIcon icon="hold" height={27} />
          </Tooltip>
        </HoldTimelineDot>
      );
    case 'skipped':
      return <SkippedTimelineDot />;
    default:
      return <FutureTimelineDot />;
  }
};

export default JobMilestoneDot;

const CancelledTimelineDot = styled(TimelineDot)`
  outline: 2px solid ${({ theme }) => theme.palette.grey[600]};
  color: ${({ theme }) => theme.palette.grey[600]};
  background-color: transparent;
  padding: ${({ theme }) => theme.spacing(0.25)};
`;

const TimerTimelineDot = styled(TimelineDot)<{ assigned: number }>`
  outline: 2px solid ${({ theme, assigned }) => (assigned ? theme.palette.info.main : theme.palette.warning.main)};
  padding: 0;
  animation: ${({ assigned }) => (assigned ? 'glow-info' : 'glow-warn')} 1s ease-in-out infinite alternate;
  border-color: transparent;
  border-width: 4px;
  background-color: transparent;
  margin-left: 0;
  margin-top: ${({ theme }) => theme.spacing(1.75)};
  margin-bottom: ${({ theme }) => theme.spacing(1.75)};
`;

const CompleteTimelineDot = styled(TimelineDot)`
  border-width: 3px;
  align-self: center;
`;

const SkippedTimelineDot = styled(TimelineDot)`
  align-self: center;
  width: 16px;
  height: 16px;
  background-size: 8px 8px;
  ${({ theme }) => `
    background-image: linear-gradient(45deg, ${theme.palette.default.main} 25%, ${theme.palette.grey[600]} 25%, ${theme.palette.grey[600]} 50%, ${theme.palette.default.main} 50%, ${theme.palette.default.main} 75%, ${theme.palette.grey[600]} 75%, ${theme.palette.grey[600]} 100%);
  `}
`;

const FutureTimelineDot = styled(TimelineDot)`
  color: ${({ theme }) => theme.palette.default.main};
  align-self: center;
  border-width: 3px;
  opacity: 0.3;
`;

const FailedTimelineDot = styled(TimelineDot)`
  color: ${({ theme }) => theme.palette.error.main};
  padding: 2px;
  outline: 2px solid ${({ theme }) => theme.palette.error.main};
  background: none;
  svg {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

const HoldTimelineDot = styled(TimelineDot)`
  background: none;
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding: 0;
  color: ${({ theme }) => theme.palette.warning.main};
`;

const FinalTimelineDot = styled(TimelineDot)`
  margin-right: ${({ theme }) => theme.spacing(-1.25)};
  margin-left: -3px;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.palette.text.primary};
  outline: 2px solid ${({ theme }) => theme.palette.success.main};
  padding: 0;
  border-color: transparent;
  border-width: 4px;
  background-color: transparent;
  svg {
    border-radius: 50%;
    background-color: ${({ theme }) => theme.palette.success.main};
    color: ${({ theme }) => theme.palette.getContrastText(theme.palette.text.primary)};
    padding: ${({ theme }) => theme.spacing(0.25)};
  }
`;
